import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  ChevronDown,
  User as UserIcon,
  Code,
  Cloud,
  Smartphone,
  Shield,
  Settings,
  Building,
  Zap,
  Store,
} from "lucide-react";
import "./ModernHeader.css";

interface NavigationItem {
  title: string;
  type: "link" | "mega";
  href?: string;
  path?: string;
  columns?: {
    title: string;
    items: {
      title: string;
      href: string;
      description?: string;
      icon?: React.ReactNode;
    }[];
  }[];
}

const ModernHeader: React.FC = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  // Navigation data for DIS-Site
  const navigationData: NavigationItem[] = [
    {
      title: "Home",
      type: "link",
      href: "/",
      path: "/",
    },
    {
      title: "Our Products",
      type: "mega",
      columns: [
        {
          title: "Development",
          items: [
            {
              title: "Web Development",
              href: "/software-dev",
              description: "Custom web applications and solutions",
              icon: <Code size={18} />,
            },
            {
              title: "Mobile Applications",
              href: "/mobile-apps",
              description: "iOS and Android app development",
              icon: <Smartphone size={18} />,
            },
          ],
        },
        {
          title: "Enterprise Solutions",
          items: [
            {
              title: "Microsoft Solutions",
              href: "/microsoft-solutions",
              description: "Microsoft 365 and Azure services",
              icon: <Cloud size={18} />,
            },
            {
              title: "Load Balancing",
              href: "/edge-nexus",
              description: "EdgeNexus load balancing solutions",
              icon: <Settings size={18} />,
            },
            {
              title: "Fiscal Solutions",
              href: "/fiscal-services",
              description: "Financial management systems",
              icon: <Shield size={18} />,
            },
            {
              title: "OmniMart",
              href: "/omni-mart",
              description: "Enterprise retail management platform ",
              icon: <Store size={18} />,
            },
          ],
        },
        {
          title: "Specialized",
          items: [
            {
              title: "Automated Form Processing",
              href: "/power-pages",
              description: "Microsoft Power Platform automations",
              icon: <Zap size={18} />,
            },
            {
              title: "GSC Guest House",
              href: "/gsc-guest-house",
              description: "Guest house management system",
              icon: <Building size={18} />,
            },
            {
              title: "eGRC Solutions",
              href: "/egrc-solutions",
              description: "Enterprise governance and compliance",
              icon: <Shield size={18} />,
            },
          ],
        },
      ],
    },
    {
      title: "Blogs",
      type: "link",
      href: "/blog-posts",
      path: "/blog-posts",
    },
    {
      title: "Our Team",
      type: "link",
      href: "/team",
      path: "/team",
    },
    {
      title: "Contact Us",
      type: "link",
      href: "/contact",
      path: "/contact",
    },
  ];

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add("mobile-menu-open");
    } else {
      document.body.classList.remove("mobile-menu-open");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("mobile-menu-open");
    };
  }, [isMobileMenuOpen]);

  // Get active section based on current path
  const getActiveSection = (): string => {
    const currentPath = location.pathname;

    // Check if it's a product page
    const isProductPage = navigationData
      .find((item) => item.type === "mega")
      ?.columns?.some((col) =>
        col.items.some((item) => item.href === currentPath),
      );

    if (isProductPage) return "Our Products";

    // Check for direct navigation match
    const directMatch = navigationData.find(
      (item) => item.path === currentPath,
    );
    return directMatch?.title || "Home";
  };

  const activeSection = getActiveSection();

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  // Desktop Dropdown Component
  const DesktopDropdown: React.FC<{ item: NavigationItem }> = ({ item }) => {
    if (item.type === "mega" && item.columns) {
      return (
        <div className="desktop-dropdown">
          <div className="dropdown-content">
            {item.columns.map((col) => (
              <div key={col.title} className="dropdown-column">
                <h3 className="column-title">{col.title}</h3>
                <div className="column-items">
                  {col.items.map((subItem) => (
                    <Link
                      key={subItem.title}
                      to={subItem.href}
                      className="dropdown-item"
                      onClick={() => setActiveDropdown(null)}
                    >
                      {subItem.icon && (
                        <div className="item-icon">{subItem.icon}</div>
                      )}
                      <div className="item-content">
                        <div className="item-title">{subItem.title}</div>
                        {subItem.description && (
                          <p className="item-description">
                            {subItem.description}
                          </p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  // Mobile Navigation Component
  const MobileNav: React.FC = () => {
    const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
      {},
    );

    const toggleExpand = (title: string) => {
      setExpandedItems((prev) => ({ ...prev, [title]: !prev[title] }));
    };

    return (
      <>
        {/* Overlay */}
        <div
          className={`mobile-overlay ${isMobileMenuOpen ? "active" : ""}`}
          onClick={() => setIsMobileMenuOpen(false)}
        />

        {/* Drawer */}
        <div className={`mobile-drawer ${isMobileMenuOpen ? "open" : ""}`}>
          <div className="mobile-header">
            {/* <div className="mobile-logo">
              <img src="/images/main-logo.png" alt="DIS Logo" />
              <span>Diamond IS</span>
            </div> */}
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="mobile-close"
            >
              <X size={20} />
            </button>
          </div>

          <div className="mobile-content">
            {navigationData.map((item) => (
              <div key={item.title} className="mobile-nav-item">
                {item.type === "link" ? (
                  <Link
                    to={item.href!}
                    className="mobile-nav-link"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.title}
                  </Link>
                ) : (
                  <>
                    <button
                      onClick={() => toggleExpand(item.title)}
                      className="mobile-nav-button"
                    >
                      {item.title}
                      <ChevronDown
                        size={18}
                        className={`chevron ${expandedItems[item.title] ? "rotated" : ""}`}
                      />
                    </button>

                    {expandedItems[item.title] && (
                      <div className="mobile-submenu">
                        {item.columns?.map((col) => (
                          <div key={col.title} className="mobile-column">
                            <h4 className="mobile-column-title">{col.title}</h4>
                            {col.items.map((sub) => (
                              <Link
                                key={sub.title}
                                to={sub.href}
                                className="mobile-submenu-item"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                {sub.icon && (
                                  <span className="mobile-item-icon">
                                    {sub.icon}
                                  </span>
                                )}
                                {sub.title}
                              </Link>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Admin Section */}
          <div className="mobile-footer">
            <Link
              to="/admin-login"
              className="mobile-admin-btn"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <UserIcon size={16} />
              Admin Login
            </Link>
          </div>
        </div>
      </>
    );
  };

  return (
    <header className={`modern-header ${scrolled ? "scrolled" : ""}`}>
      <div className="header-container">
        <div className="header-content">
          {/* Logo Section */}
          <Link to="/" className="logo-section">
            <img
              src="/images/main-logo.png"
              alt="Diamond Information Systems"
              className="logo-image"
            />
            {/* <span className="logo-text">Diamond IS</span> */}
          </Link>

          {/* Desktop Navigation */}
          <nav className="desktop-nav">
            {navigationData.map((item) => (
              <div
                key={item.title}
                className="nav-item-container"
                onMouseEnter={() =>
                  item.type !== "link" && setActiveDropdown(item.title)
                }
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {item.type === "link" ? (
                  <Link
                    to={item.href!}
                    className={`nav-link ${activeSection === item.title ? "active" : ""}`}
                  >
                    {item.title}
                  </Link>
                ) : (
                  <button
                    className={`nav-button ${activeSection === item.title ? "active" : ""}`}
                  >
                    {item.title}
                    <ChevronDown
                      size={14}
                      className={`nav-chevron ${activeDropdown === item.title ? "rotated" : ""}`}
                    />
                  </button>
                )}

                {activeDropdown === item.title && item.type !== "link" && (
                  <DesktopDropdown item={item} />
                )}
              </div>
            ))}
          </nav>

          {/* Right Section */}
          <div className="header-right">
            <div className="admin-section">
              <Link to="/admin-login" className="admin-link">
                Admin Login
              </Link>
            </div>

            {/* Mobile Menu Toggle */}
            <button onClick={toggleMobileMenu} className="mobile-toggle">
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <MobileNav />
    </header>
  );
};

export default ModernHeader;
